import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Button,
  Stack,
} from '@mui/material';
import { ExpandMore, ExpandLess, Search, Refresh } from '@mui/icons-material';
import { getApiUrl } from '../utils/apiConfig';

export default function BottomPanel({ onOpenChange }) {
  const [open, setOpen] = React.useState(() => {
    try {
      const v = localStorage.getItem('wb_bottom_open');
      if (v === null || v === undefined) return false;
      return v === '1';
    } catch {
      return false;
    }
  });
  const location = useLocation();

  const initialPatientId = React.useMemo(() => {
    try {
      return localStorage.getItem('wb_patient_id') || '';
    } catch {
      return '';
    }
  }, []);

  const [patientId, setPatientId] = React.useState(initialPatientId);
  const [patientIdSource, setPatientIdSource] = React.useState(
    initialPatientId ? 'context' : 'manual'
  );
  const lastReadinessId = React.useRef('');
  const [readiness, setReadiness] = React.useState(null);
  const [aiSug, setAiSug] = React.useState(null);

  React.useEffect(() => {
    try {
      localStorage.setItem('wb_bottom_open', open ? '1' : '0');
    } catch {
      // noop
    }
  }, [open]);

  React.useEffect(() => {
    if (typeof onOpenChange === 'function') {
      onOpenChange(open);
    }
  }, [open, onOpenChange]);

  const token = React.useMemo(
    () => localStorage.getItem('_at') || localStorage.getItem('token'),
    []
  );
  const headers = React.useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const routePatientId = React.useMemo(() => {
    const path = location.pathname || '';
    const params = new URLSearchParams(location.search || '');
    const searchId = params.get('patient') || params.get('patientId');
    if (searchId) return searchId;

    const parts = path.split('/').filter(Boolean);
    const summaryIndex = parts.findIndex(p => p === 'preop-summary' || p === 'patient-summary');
    if (summaryIndex >= 0 && parts[summaryIndex + 1]) {
      return parts[summaryIndex + 1];
    }

    const patientIndex = parts.indexOf('patient');
    if (patientIndex >= 0 && parts[patientIndex + 1] && parts[patientIndex + 2] === 'summary') {
      return parts[patientIndex + 1];
    }

    return '';
  }, [location.pathname, location.search]);

  const toggle = () => setOpen(v => !v);

  const applyContextPatient = React.useCallback(nextId => {
    const normalized = String(nextId || '').trim();
    if (!normalized) return;
    setPatientIdSource('context');
    setPatientId(prev => (prev === normalized ? prev : normalized));
    setReadiness(null);
    setAiSug(null);
    try {
      localStorage.setItem('wb_patient_id', normalized);
    } catch {
      // noop
    }
  }, []);

  const fetchReadiness = React.useCallback(
    async (id, options = {}) => {
      const targetId = String(id || '').trim();
      if (!targetId) return;
      const { persist = false } = options;
      lastReadinessId.current = targetId;
      if (persist) {
        try {
          localStorage.setItem('wb_patient_id', targetId);
        } catch {
          // noop
        }
      }

      try {
        let res = await fetch(getApiUrl(`/api/readiness/${targetId}`), { headers });
        if (res.status === 404) {
          res = await fetch(getApiUrl(`/api/patients/${targetId}/readiness`), { headers });
        }
        const json = await res.json().catch(() => ({}));

        if (json && (json.success || json.status)) {
          setReadiness(json);
        } else if (json && json.data) {
          setReadiness(json.data);
        } else {
          setReadiness(null);
        }

        try {
          const aiRes = await fetch(getApiUrl(`/api/ai/enhanced/insights/${targetId}`), { headers });
          const aiJson = await aiRes.json().catch(() => null);
          setAiSug(aiJson?.data || aiJson || null);
        } catch {
          setAiSug(null);
        }
      } catch (e) {
        console.warn('[Workbench] Readiness fetch failed', e);
        setReadiness(null);
        setAiSug(null);
      }
    },
    [headers]
  );

  React.useEffect(() => {
    if (!routePatientId) return;
    applyContextPatient(routePatientId);
  }, [routePatientId, applyContextPatient]);

  React.useEffect(() => {
    const handler = event => {
      const detail = event?.detail || {};
      const nextId = detail.patientId || detail.id;
      if (!nextId) return;
      applyContextPatient(nextId);
      if (typeof detail.open === 'boolean') {
        setOpen(detail.open);
      }
    };
    globalThis.addEventListener?.('wb:bottom-panel', handler);
    return () => globalThis.removeEventListener?.('wb:bottom-panel', handler);
  }, [applyContextPatient]);

  React.useEffect(() => {
    if (!open || !patientId || patientIdSource !== 'context') return;
    if (lastReadinessId.current === patientId) return;
    fetchReadiness(patientId, { persist: true });
  }, [open, patientId, patientIdSource, fetchReadiness]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'fixed',
          right: 16,
          bottom: open
            ? 'calc(var(--wb-bottom-h, 0px) + env(safe-area-inset-bottom, 0px) + 8px)'
            : {
                xs: 'calc(var(--wb-mobile-nav-h, 0px) + env(safe-area-inset-bottom, 0px) + 8px)',
                md: 16,
              },
          zIndex: theme => theme.zIndex.tooltip + 2,
          bgcolor: theme => theme.palette.background.paper,
          borderRadius: 20,
          border: theme => `1px solid ${theme.palette.divider}`,
          boxShadow: 1,
        }}
      >
        <Tooltip title={open ? 'Hide panel' : 'Show panel'}>
          <IconButton
            size="small"
            onClick={toggle}
            aria-label={open ? 'Hide bottom panel' : 'Show bottom panel'}
          >
            {open ? <ExpandMore /> : <ExpandLess />}
          </IconButton>
        </Tooltip>
      </Box>

      {open && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            height: 220,
            borderTop: theme => `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme => theme.palette.background.paper,
            zIndex: theme => theme.zIndex.modal,
            boxShadow: 3,
          }}
        >
          <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                size="small"
                placeholder="Patient ID"
                value={patientId}
                onChange={e => {
                  setPatientIdSource('manual');
                  setPatientId(e.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') fetchReadiness(patientId, { persist: true });
                }}
              />
              <Stack direction="row" spacing={1}>
                <Button size="small" startIcon={<Refresh />} onClick={() => fetchReadiness(patientId, { persist: true })}>
                  Refresh
                </Button>
              </Stack>
            </Box>

            <List dense sx={{ flex: 1 }}>
              {Array.isArray(readiness?.tasks) && readiness.tasks.length > 0 ? (
                readiness.tasks.map((t, i) => (
                  <ListItem key={`task-${i}`}>
                    <ListItemText
                      primary={t.name || t.displayName || 'Task'}
                      secondary={`${t.required ? 'Required' : 'Optional'} • ${
                        t.completed ? 'Completed' : 'Pending'
                      }`}
                    />
                  </ListItem>
                ))
              ) : (
                <>
                  <ListItem>
                    <ListItemText
                      primary={
                        readiness ? `Status: ${readiness.status || 'pending'}` : 'Enter a patient ID to load readiness'
                      }
                    />
                  </ListItem>
                  {aiSug?.recommendations?.length > 0 && (
                    <>
                      <ListItem>
                        <ListItemText
                          primary="AI Suggestions"
                          secondary={`${aiSug.recommendations.length} items`}
                        />
                      </ListItem>
                      {aiSug.recommendations.slice(0, 5).map((r, idx) => (
                        <ListItem key={`ai-sug-${idx}`}>
                          <ListItemText
                            primary={r.name || r.action || 'Recommendation'}
                            secondary={`${r.type || r.category || 'action'} • ${
                              r.rationale || r.reason || ''
                            }`}
                          />
                        </ListItem>
                      ))}
                    </>
                  )}
                </>
              )}
            </List>
          </Box>
        </Box>
      )}
    </Box>
  );
}
